import { FC } from 'react'
import { Box, Typography } from '@mui/material'
import { ArrowUpIcon } from '@heroicons/react/24/outline'
import theme from 'theme'

import { useThemeColors } from 'hooks/useThemeColors'
import { ChartContainer, ValueWrapper, OverPerformanceIndicator } from './halfDonutChart.styles'

interface Props {
  value: number
  size?: number
  lastMonthChange?: number | null
}

const HalfDonutChart: FC<Props> = ({ value, size = 200, lastMonthChange }) => {
  const themeColor = useThemeColors()
  const radius = size / 2 - 10
  const strokeWidth = 30
  const normalizedRadius = radius - strokeWidth / 2
  const circumference = normalizedRadius * Math.PI

  const clampedValue = Math.min(value, 100)
  const overValue = Math.max(value - 100, 0)

  const progressLength = (clampedValue / 100) * circumference

  return (
    <ChartContainer size={size}>
      <svg height={200} width={size}>
        <path
          d={`M ${strokeWidth / 2},${size / 2} A ${normalizedRadius},${normalizedRadius} 0 0,1 ${
            size - strokeWidth / 2
          },${size / 2}`}
          fill="none"
          stroke={themeColor('gauge-track')}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />

        {clampedValue > 0 && (
          <path
            d={`M ${strokeWidth / 2},${size / 2} A ${normalizedRadius},${normalizedRadius} 0 0,1 ${
              size - strokeWidth / 2
            },${size / 2}`}
            fill="none"
            stroke={themeColor('cta')}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={
              clampedValue === 100 ? `${circumference + 50} 0` : `${progressLength} ${circumference * 2}`
            }
            style={{
              transition: 'stroke-dasharray 0.8s ease-in-out'
            }}
          />
        )}
      </svg>

      <ValueWrapper>
        <Typography className="value">{value}%</Typography>
        {(lastMonthChange !== undefined || overValue > 0) && (
          <div className="indicators-wrapper">
            {lastMonthChange !== undefined && (
              <div className="change-indicator">
                {lastMonthChange == null ? (
                  <span className="since-label">--- since last month</span>
                ) : (
                  <>
                    <div
                      className={`change-value ${
                        lastMonthChange > 0 || lastMonthChange === 0 ? 'positive' : 'negative'
                      }`}
                    >
                      {lastMonthChange !== 0 && <ArrowUpIcon className="arrow-icon" />}
                      <span className="change-text">{Math.abs(lastMonthChange)}%</span>
                    </div>
                    <span className="since-label">since last month</span>
                  </>
                )}
              </div>
            )}
            {overValue > 0 && (
              <OverPerformanceIndicator>
                <Box className="icon-text">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
                      fill={theme.baseColors.info[30]}
                    />
                  </svg>
                  <Typography className="over-value">{overValue.toFixed(0)}%</Typography>
                </Box>
                <Typography className="over-text">Above Target</Typography>
              </OverPerformanceIndicator>
            )}
          </div>
        )}
      </ValueWrapper>
    </ChartContainer>
  )
}

export default HalfDonutChart
